import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, Package, Shield, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { mockData } from '../mock';

const Home = () => {
  const { organization, services } = mockData;
  const [copied, setCopied] = useState(false);

  const iconMap = {
    heart: Heart,
    package: Package,
    shield: Shield
  };

  const serviceColors = [
    { bg: 'bg-red-100', text: 'text-red-600' },
    { bg: 'bg-olive-100', text: 'text-olive-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
  ];

  const copyEmail = () => {
    navigator.clipboard.writeText('beahero@bitsofkindness.ca');
    setCopied(true);
    toast.success('Email copied to clipboard!');
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-mauve-100 to-white py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl !font-['Tahoma'] !font-bold mb-6 text-darkslate-700">
            {organization.name}
          </h1>
          <p className="text-xl md:text-2xl text-darkslate-500 mb-8 max-w-3xl mx-auto">
            We're here for the pets and families who need us most. By providing essential supplies and support, we aim to ease hardship, keep pets in loving homes, and give every animal a better chance at a happy life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8" data-testid="view-donation-list-btn">
                View Donation List
              </Button>
            </Link>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSeOVODJjWPU2frnYvpHIOt_8DbiqrVXICIQreiph4ZnKdNh4Q/viewform?usp=header"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8" data-testid="get-help-btn">
                Click Here to Get Help
              </Button>
            </a>
          </div>

          {/* E-Transfer Donation Section */}
          <div className="mt-8 max-w-2xl mx-auto">
            <Card className="border-2 border-amber-500 bg-white shadow-lg">
              <CardContent className="p-6">
                <div className="flex flex-col items-center gap-4">
                  <h3 className="text-xl font-bold text-darkslate-700 text-center">
                    Send E-Transfer Donation
                  </h3>
                  <div className="flex flex-col sm:flex-row items-center gap-3 w-full justify-center">
                    <div className="bg-amber-50 border-2 border-amber-500 rounded-lg px-6 py-3">
                      <p className="font-semibold text-amber-700 text-lg">
                        beahero@bitsofkindness.ca
                      </p>
                    </div>
                    <Button
                      onClick={copyEmail}
                      className="bg-amber-500 hover:bg-amber-600 text-white border-2 border-black"
                      data-testid="copy-email-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="mr-2 h-4 w-4" />
                          Copy Email
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-darkslate-700 mb-4">
            How We Help
          </h2>
          <p className="text-center text-darkslate-400 mb-12 max-w-2xl mx-auto">
            We're committed to keeping pets with their loving families through various support services.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {services.map((service, idx) => {
              const IconComponent = iconMap[service.icon];
              const colors = serviceColors[idx % serviceColors.length];
              return (
                <Card key={service.id} className="border-2 border-mauve-200 hover:border-olive-400 transition-all duration-300 hover:shadow-lg">
                  <CardHeader>
                    <div className={`w-14 h-14 ${colors.bg} rounded-lg flex items-center justify-center mb-4`}>
                      <IconComponent className={`h-7 w-7 ${colors.text}`} />
                    </div>
                    <CardTitle className="text-xl text-darkslate-700">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-darkslate-400 text-base">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>);
            })}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 px-4 bg-mauve-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-darkslate-700 mb-4">
            Serving {organization.location}
          </h2>
          <p className="text-lg text-darkslate-500 mb-6">
            We prioritize helping families in {organization.serviceArea}. Every pet deserves to stay in a loving home.
          </p>
          <Link to="/about">
            <Button variant="outline" className="border-darkslate-300 text-darkslate-600 hover:bg-mauve-100">
              Learn More About Us
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-darkslate-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Make a Difference Today
          </h2>
          <p className="text-lg mb-8 text-mauve-200">
            Your donations help keep beloved pets with their families. Every contribution matters.
          </p>
          <Link to="/donate">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8">
              See What We Need
            </Button>
          </Link>
        </div>
      </section>
    </div>);
};

export default Home;
